/** 统一 API 客户端。
 *
 * 形状抄 `hummingbot/condor` 的两个小函数：`apiFetch`（一次请求）与
 * `query`（GET + 参数）。**参数序列化只写一次**是这套形状最值钱的一条注释——
 * 每个屏各自拼 query string，迟早出现一处忘了 `encodeURIComponent`。
 */

import { ApiError, ContractError } from "./errors";
import { readApiToken, UNAUTHORIZED_HINT } from "./token";

/** 契约前缀（§3）。 */
export const API_BASE = "/api/v1";

export type QueryValue = string | number | boolean | null | undefined;

export interface ApiDeps {
  /** 注入点：单测用假 fetch，生产用全局 fetch。 */
  fetcher?: typeof fetch;
  /** 覆盖前缀，便于把操作台指向别的 keel-server。 */
  baseUrl?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
  /** 写接口令牌；不给就读本机存的那一份（设置屏填写）。只读请求一律不带。 */
  token?: string | null;
}

const DEFAULT_TIMEOUT_MS = 10_000;

/** 拼路径与查询串。空值（`null` / `undefined` / `""`）不进 query——
 * 「没填」与「填了空串」在契约里是两回事，不要合并。 */
export function apiPath(path: string, params?: Record<string, QueryValue>): string {
  if (!params) return path;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `${path}?${parts.join("&")}` : path;
}

export function apiUrl(path: string, deps: ApiDeps = {}): string {
  return `${deps.baseUrl ?? API_BASE}${path}`;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

interface RequestOptions extends ApiDeps {
  method: "GET" | "POST";
  body?: unknown;
}

/** 一次请求。抛的一定是 `ApiError`（含子类 `ContractError`），调用点不必再分情况。 */
export async function apiFetch<T>(path: string, options: RequestOptions): Promise<T> {
  const url = apiUrl(path, options);
  const fetcher = options.fetcher ?? globalThis.fetch;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onAbort = () => controller.abort();
  options.signal?.addEventListener("abort", onAbort, { once: true });

  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.method !== "GET") {
    // 写请求统一带令牌（#5004）；本机没存时不带，由服务端 401 → 下面翻成「去设置屏填写」。
    const token = options.token !== undefined ? options.token : readApiToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetcher(url, {
      method: options.method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    });
  } catch (cause) {
    throw new ApiError({
      kind: timedOut ? "timeout" : "network",
      message: timedOut
        ? `请求超过 ${timeoutMs} ms 未返回`
        : options.signal?.aborted
          ? "请求被取消"
          : `请求未能发出：${cause instanceof Error ? cause.message : String(cause)}`,
      url,
      cause,
    });
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", onAbort);
  }

  const payload = await readBody(response);
  if (!response.ok) {
    throw new ApiError({
      kind: "http",
      status: response.status,
      message: describeHttp(response, payload),
      url,
      detail: typeof payload === "string" ? payload.slice(0, 400) : undefined,
    });
  }
  return payload as T;
}

function describeHttp(response: Response, payload: unknown): string {
  const reason = payload && typeof payload === "object" && "detail" in payload
    ? String((payload as { detail: unknown }).detail)
    : response.statusText;
  const text = `HTTP ${response.status}${reason ? `：${reason}` : ""}`;
  // 写接口令牌缺失 / 不符：直接告诉操作员去哪里修，而不是只给一个状态码。
  return response.status === 401 ? `${UNAUTHORIZED_HINT}（${text}）` : text;
}

/** 只读入口的取数函数。屏只认它，不认 `fetch`。 */
export function query<T>(path: string, params?: Record<string, QueryValue>, deps: ApiDeps = {}): Promise<T> {
  return apiFetch<T>(apiPath(path, params), { ...deps, method: "GET" });
}
export function post<T>(path: string, body: unknown, deps: ApiDeps = {}): Promise<T> {
  return apiFetch<T>(path, { ...deps, method: "POST", body });
}

/** 契约守卫。不满足即抛 `ContractError`，由屏翻译成错误态。 */
export function expectRecord(value: unknown, url: string, what: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ContractError(`${what} 应为对象，实际是 ${typeName(value)}`, url, value);
  }
  return value as Record<string, unknown>;
}

export function expectList(
  body: unknown,
  key: string,
  url: string,
  what: string,
): Record<string, unknown>[] {
  // 契约写的是 `{"orders": [...]}`；裸数组也能读，但别的形状一律不猜。
  const list = Array.isArray(body) ? body : expectRecord(body, url, `${what} 响应`)[key];
  if (!Array.isArray(list)) {
    throw new ContractError(`${what} 响应缺少数组字段 \`${key}\``, url, body);
  }
  return list.map((item, index) => expectRecord(item, url, `${what}[${index}]`));
}

export function requireBoolean(source: Record<string, unknown>, key: string, url: string): boolean {
  const value = source[key];
  if (typeof value !== "boolean") {
    throw new ContractError(`字段 \`${key}\` 应为布尔，实际是 ${typeName(value)}`, url, source);
  }
  return value;
}

export function requireNumber(source: Record<string, unknown>, key: string, url: string): number {
  const value = source[key];
  if (typeof value !== "number" || Number.isNaN(value)) {
    throw new ContractError(`字段 \`${key}\` 应为数字，实际是 ${typeName(value)}`, url, source);
  }
  return value;
}

export function optionalNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && !Number.isNaN(value) ? value : null;
}

export function optionalString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === "string" ? value : null;
}

export function requireString(source: Record<string, unknown>, key: string, url: string): string {
  const value = source[key];
  if (typeof value !== "string") {
    throw new ContractError(`字段 \`${key}\` 应为字符串，实际是 ${typeName(value)}`, url, source);
  }
  return value;
}

export function typeName(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "数组";
  return typeof value;
}
